import { Location } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AnvandarRoll, AnvandareDTO, EXTERN_ROLES, FLAT_ROLES, INTERN_ROLES } from '../anvandare.models';
import { AnvandareService } from '../anvandare.service';
import { Role, RoleLabels } from '../anvandare.models';
import { MessageService } from 'primeng/api';
import { AnvandareOrganisation } from 'src/app/auth/services/auth.model';
import { remove, update } from 'lodash';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AddAnvandareOrganisationComponent } from './add-anvandare-organisation/add-anvandare-organisation.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmRemoveOrganisationComponent } from './confirm-remove-organisation/confirm-remove-organisation.component';
import { SessionService } from 'src/app/auth/services/session.service';
import { AuthorizationService } from 'src/app/auth/services/authorization.service';

export interface AnvandarRollCheck {
  organisationId: string;
  roll: string;
  checked: boolean;
  show: boolean;
}

@Component({
  selector: 'app-edit-anvandare',
  templateUrl: './edit-anvandare.component.html',
  styleUrl: './edit-anvandare.component.scss',
  standalone: false,
})
export class EditAnvandareComponent implements OnInit {
  public location = inject(Location);
  private fb = inject(FormBuilder);
  private anvandareService = inject(AnvandareService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private dialog = inject(DialogService);
  private destroy = inject(DestroyRef);
  private session = inject(SessionService);
  private authorize = inject(AuthorizationService);
  private adminUser!: AnvandareDTO;

  @Input() id!: string;
  protected roles = FLAT_ROLES;
  protected filteredRoles: string[] = this.roles.slice();
  protected anvandare!: AnvandareDTO;
  protected roller: string[] = [];
  protected editAnvandareForm = this.fb.nonNullable.group({
    id: ['', Validators.required],
    anvandarnamn: [''],
    anvandartyp: ['', Validators.required],
    efternamn: ['', Validators.required],
    epost: [''],
    fornamn: ['', Validators.required],
    organisation: [''],
    organisationId: [''],
    status: ['', Validators.required],
    telefon: [''],
    roller: this.fb.nonNullable.group({}),
    anvandareOrganisationer: this.fb.array([]),
  });
  protected anvandareOrganisationOptions!: AnvandareOrganisation[];
  protected selectedAnvandareOrganisation!: AnvandareOrganisation;
  protected selectedOrganisationRoles!: AnvandarRollCheck[];
  private addAnvandareOrganisationDialog: DynamicDialogRef | null = null;
  private confirmRemoveOrganisationDialog: DynamicDialogRef | null = null;
  disbleForExternAdminFlag = false;
  protected canEditOrgs = false;
  public RoleLabels = RoleLabels;

  ngOnInit(): void {
    this.anvandareService
      .getUserById(this.session.getUserId())
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((admin) => {
        this.adminUser = admin;
        this.addAnvandareOrganisationFormControl();
        this.updateSelectedOrg(this.anvandare.anvandareOrganisationer[0], true);
      });
    this.canEditOrgs = this.session.getUserType() === 'Intern';
    this.anvandare = this.location.getState() as AnvandareDTO;
    this.anvandareOrganisationOptions = [...this.anvandare.anvandareOrganisationer];
    this.selectedAnvandareOrganisation = this.anvandareOrganisationOptions[0];
    this.selectedOrganisationRoles = [];
    this.initAnvandarRoller();
    this.editAnvandareForm.patchValue({
      id: this.anvandare.id,
      anvandarnamn: this.anvandare.anvandarnamn,
      anvandartyp: this.anvandare.anvandartyp,
      efternamn: this.anvandare.efternamn,
      epost: this.anvandare.epost,
      fornamn: this.anvandare.fornamn,
      organisation: this.anvandare.organisation,
      organisationId: this.anvandare.organisationId,
      status: this.anvandare.status,
      telefon: this.anvandare.telefon,
    });
  }

  initAnvandarRoller() {
    const validRoles = this.anvandare.anvandartyp === 'Intern' ? INTERN_ROLES : EXTERN_ROLES;
    this.anvandareOrganisationOptions.forEach((option) => {
      validRoles.forEach((role) => {
        this.selectedOrganisationRoles.push({
          organisationId: option.organisationId,
          roll: role,
          checked:
            this.anvandare.anvandarRoller.find((x) => x.organisationId === option.organisationId && x.roll === role) !=
            undefined
              ? true
              : false,
          show: option.organisationId === this.selectedAnvandareOrganisation.organisationId,
        });
      });
    });
  }

  addAnvandareOrganisationFormControl() {
    this.anvandare.anvandareOrganisationer.forEach((org) => {
      const anvandareOrg = this.fb.group({
        epost: [
          org.epost ?? '',
          [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')],
        ],
        organisation: org.organisation,
        organisationId: org.organisationId,
        telefon: [org.telefon ?? '', [Validators.required, Validators.pattern('^[0-9]*$')]],
        titel: [org.titel ?? ''],
        isLoggedIn: 0,
      });
      this.getAnvandareOrganisationer().push(anvandareOrg);
    });
  }
  getAnvandareOrganisationer() {
    return this.editAnvandareForm.get('anvandareOrganisationer') as FormArray;
  }

  updateSelectedOrg(org: AnvandareOrganisation, wtf?: any) {
    this.selectedAnvandareOrganisation = org;
    this.selectedOrganisationRoles = this.selectedOrganisationRoles.map((x) => {
      return { ...x, checked: x.checked, show: x.organisationId === this.selectedAnvandareOrganisation.organisationId };
    });
    this.disableEditForExternalAdmin();
  }

  disableEditForExternalAdmin() {
    if (this.canEditOrgs) {
      this.disbleForExternAdminFlag = false;
    } else {
      const adminOrgs = this.adminUser.anvandarRoller
        .filter((roll) => this.authorize.roleIsAdmin(roll.roll))
        .map((roll) => roll.organisationId);
      const adminOrg = this.session.anvandarOrganisationer();
      this.disbleForExternAdminFlag = !adminOrgs.some((y) => y === this.selectedAnvandareOrganisation.organisationId);
    }
  }


  removeOrg() {
    const dialogRef = this.dialog.open(ConfirmRemoveOrganisationComponent, {
      header: 'Ta bort organisationstillhörighet',
      width: '500px',
    });
    if (!dialogRef) {
      return;
    }
    this.confirmRemoveOrganisationDialog = dialogRef;
    dialogRef.onClose.pipe(takeUntilDestroyed(this.destroy)).subscribe((remove) => {
      if (!remove) {
        return;
      }
      const index = this.anvandareOrganisationOptions.indexOf(this.selectedAnvandareOrganisation);
      const neworg = this.anvandareOrganisationOptions.find(
        (x) => x.organisationId !== this.selectedAnvandareOrganisation.organisationId,
      ) as AnvandareOrganisation;

      this.removeAnvandarRoller(this.selectedAnvandareOrganisation.organisationId, neworg.organisationId);
      this.updateSelectedOrg(neworg);
      this.getAnvandareOrganisationer().removeAt(index);
      this.anvandareOrganisationOptions.splice(index, 1);
    });
  }

  removeAnvandarRoller(removedOrgId: string, newlySelectedOrgId: string) {
    this.selectedOrganisationRoles = this.selectedOrganisationRoles
      .filter((roll) => roll.organisationId !== removedOrgId)
      .map((x) => {
        return { ...x, checked: x.checked, show: x.organisationId === newlySelectedOrgId };
      });
  }

  getRoleLabel(roll: string): string {
    return RoleLabels[roll as Role] || roll; // Return the label or the original roll if not found
  }

  save = () => {
    const userRoles: AnvandarRoll[] = this.selectedOrganisationRoles
      .filter((roll) => roll.checked)
      .map((roll) => {
        return {
          organisationId: roll.organisationId,
          roll: roll.roll,
        };
      });

    const updatedAnvandare: AnvandareDTO = {
      ...(this.editAnvandareForm.value as AnvandareDTO),
      roller: [],
      anvandarRoller: userRoles,
    };

    this.updateAnvandare(updatedAnvandare);
  };

  protected cancel() {
    this.router.navigate(['/anvandare']);
  }

  protected openAddAnvandareOrganisationDialog() {
    const dialogRef = this.dialog.open(AddAnvandareOrganisationComponent, {
      data: { anvandare: this.anvandare },
      header: 'Lägg till organisationstillhörighet',
      width: '600px',
    });
    if (!dialogRef) {
      return;
    }
    this.addAnvandareOrganisationDialog = dialogRef;

    dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((result: { org: AnvandareOrganisation; roles: AnvandarRoll[] }) => {
        if (!result) {
          return;
        }
        const updatedAnvandare = { ...this.anvandare };
        updatedAnvandare.anvandareOrganisationer.push(result.org);
        updatedAnvandare.anvandarRoller.push(...result.roles);

        this.updateAnvandare(updatedAnvandare);
      });
  }

  private updateAnvandare(updatedAnvandare: AnvandareDTO) {
    this.anvandareService.updateAnvandare(updatedAnvandare).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          detail: 'Användare har updaterats',
        });
        this.router.navigate(['/anvandare']);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          detail: 'Något gick fel vid uppdatering av användare',
        });
      },
    });
  }
}
